import React, { Fragment, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Col, Container, FormGroup, Input, Label, Row, Card, CardBody
} from "reactstrap";
import { FaCheck, FaTimes, FaEye } from "react-icons/fa";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { baseURL } from "../../../../Services/api/baseURL";
import Loader from "../../../Loader/Loader";
import CommonModal from "../../../UiKits/Modals/common/modal";

const STATUS_COLORS = {
  pending: { bg: "#fff8e1", text: "#f59e0b", label: "Pending" },
  approved: { bg: "#e8f5e9", text: "#16a34a", label: "Approved" },
  rejected: { bg: "#fce4ec", text: "#dc2626", label: "Rejected" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text,
      padding: "3px 10px", borderRadius: "999px",
      fontWeight: 600, fontSize: "0.75rem",
    }}>
      {s.label}
    </span>
  );
};

const STATUS_TABS = ["all", "pending", "approved", "rejected"];
const getToken = () => JSON.parse(localStorage.getItem("token"));

export default function B2BAddressApproval() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("pending");
  const [search, setSearch] = useState("");

  // Reject modal
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  // View modal
  const [viewModal, setViewModal] = useState(false);
  const [viewAddr, setViewAddr] = useState(null);

  const fetch = async (page = 1, limit = perPage, tab = activeTab) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (tab !== "all") params.append("approval_status", tab);
      if (search) params.append("search", search);

      const res = await axios.get(
        `${baseURL}/api/address/b2b-addresses?${params}`,
        { headers: { Authorization: getToken() } }
      );
      setData(res.data?.data || []);
      setTotalRows(res.data?.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  console.log("data", data);

  useEffect(() => { fetch(1, perPage, activeTab); }, [activeTab]);

  const handleApprove = async (row) => {
    const result = await Swal.fire({
      title: `Approve this address?`,
      html: `<p class="text-sm text-gray-600">${row.address}</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      confirmButtonText: "Yes, approve",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.patch(
        `${baseURL}/api/address/approve-address/${row._id}`,
        {},
        { headers: { Authorization: getToken() } }
      );
      fetch(1, perPage, activeTab);
      Swal.fire({ title: "Address approved!", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (e) {
      Swal.fire({ title: e?.response?.data?.message || "Failed", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  const openReject = (row) => {
    setRejectTarget(row);
    setRejectNote("");
    setRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    try {
      await axios.patch(
        `${baseURL}/api/address/reject-address/${rejectTarget._id}`,
        { note: rejectNote },
        { headers: { Authorization: getToken() } }
      );
      setRejectModal(false);
      fetch(1, perPage, activeTab);
      Swal.fire({ title: "Address rejected", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (e) {
      Swal.fire({ title: "Failed", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  const columns = [
    {
      name: "Business",
      selector: (r) => r.business_name,
      sortable: true,
      cell: (r) => (
        <div>
          <p className="mb-0 text-sm font-semibold text-[#1D2433]">
            {r.business_name || "—"}
          </p>
          <p className="mb-0 text-xs text-[#1D243399]">
            {r.user_id?.phoneNumber || ""}
          </p>
        </div>
      ),
    },
    {
      name: "Address",
      selector: (r) => r.address,
      cell: (r) => (
        <div>
          <p className="mb-0 text-sm font-medium">{r.first_name} {r.last_name}</p>
          <p className="mb-0 text-xs text-[#1D243399]">{r.address}</p>
          <p className="mb-0 text-xs text-[#1D243399]">{r.city} {r.state} {r.addressPincode}</p>
        </div>
      ),
      grow: 2,
    },
    {
      name: "User",
      center: true,
      cell: (r) => (
        <div className="text-xs text-center">
          <p className="mb-0">{r.user_id?.phoneNumber || "—"}</p>
          <p className="mb-0 text-[#1D243399]">{r.user_id?.email || ""}</p>
        </div>
      ),
    },
    {
      name: "Phone",
      center: true,
      selector: (r) => r.addressPhoneNumber,
      cell: (r) => <span className="text-xs">{r.addressPhoneNumber}</span>,
    },
    {
      name: "Status",
      center: true,
      cell: (r) => <StatusBadge status={r.approval_status} />,
    },
    {
      name: "Added",
      center: true,
      cell: (r) => (
        <span className="text-xs">
          {moment(r.createdAt).format("DD/MM/YY HH:mm")}
        </span>
      ),
    },
    {
      name: "Actions",
      center: true,
      cell: (r) => (
        <div className="d-flex gap-1">
          <button
            type="button"
            title="View"
            onClick={() => { setViewAddr(r); setViewModal(true); }}
            style={{
              background: "#f3f4f6", border: "none",
              borderRadius: "6px", padding: "5px 8px", cursor: "pointer",
            }}
          >
            <FaEye size={13} />
          </button>
          {r.approval_status !== "approved" && (
            <button
              type="button"
              title="Approve"
              onClick={() => handleApprove(r)}
              style={{
                background: "#e8f5e9", color: "#16a34a", border: "none",
                borderRadius: "6px", padding: "5px 8px", cursor: "pointer",
              }}
            >
              <FaCheck size={13} />
            </button>
          )}
          {r.approval_status !== "rejected" && (
            <button
              type="button"
              title="Reject"
              onClick={() => openReject(r)}
              style={{
                background: "#fce4ec", color: "#dc2626", border: "none",
                borderRadius: "6px", padding: "5px 8px", cursor: "pointer",
              }}
            >
              <FaTimes size={13} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (

    <Fragment>
      <Container fluid={true} style={{ paddingTop: '30px' }}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody style={{ padding: '15px' }}>
                <Fragment>
                  {/* Header */}
                  <Row className="pb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="fw-bolder mb-0">B2B Delivery Address Approvals</h5>
                        <small className="text-muted">
                          Approve or reject customer delivery addresses based on your delivery zones
                        </small>
                      </div>
                      <div className="form-group position-relative search_outer d-flex align-items-center mb-0">
                        <i className="fa fa-search" />
                        <input
                          className="form-control border-0"
                          value={search}
                          onChange={(e) => { setSearch(e.target.value); }}
                          onKeyDown={(e) => e.key === "Enter" && fetch(1, perPage, activeTab)}
                          type="text"
                          placeholder="Search address, name…"
                        />
                      </div>
                    </div>
                  </Row>

                  {/* Status tabs */}
                  <div className="d-flex gap-2 mb-3 flex-wrap">
                    {STATUS_TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveTab(tab)}
                        style={{
                          padding: "5px 16px", borderRadius: "999px", border: "none",
                          fontWeight: 600, fontSize: "0.8rem", cursor: "pointer",
                          background: activeTab === tab ? "#D3178A" : "#f3f4f6",
                          color: activeTab === tab ? "#fff" : "#374151",
                          textTransform: "capitalize",
                        }}
                      >
                        {tab === "all" ? "All" : STATUS_COLORS[tab]?.label}
                      </button>
                    ))}
                  </div>

                  {/* Table */}
                  <DataTable
                    data={data}
                    columns={columns}
                    pagination
                    paginationServer
                    progressPending={loading}
                    progressComponent={<Loader />}
                    paginationTotalRows={totalRows}
                    onChangePage={(p) => fetch(p, perPage, activeTab)}
                    onChangeRowsPerPage={(newPer, p) => { setPerPage(newPer); fetch(p, newPer, activeTab); }}
                    noDataComponent={
                      <p className="py-5 text-center text-[#1D243399]">
                        No {activeTab === "all" ? "" : activeTab} addresses found.
                      </p>
                    }
                  />

                  {/* ── View detail modal ─────────────────────────────────────── */}
                  <CommonModal
                    isOpen={viewModal}
                    title="Address Details"
                    toggler={() => setViewModal(false)}
                    size="md"
                  >
                    {viewAddr && (
                      <Container>
                        <Row>
                          <Col xxl={12} className="mb-3">
                            <StatusBadge status={viewAddr.approval_status} />
                            {viewAddr.approval_note && (
                              <p className="text-xs text-err mt-2">Note: {viewAddr.approval_note}</p>
                            )}
                          </Col>

                          {[
                            ["Name", `${viewAddr.first_name} ${viewAddr.last_name}`],
                            ["Phone", viewAddr.addressPhoneNumber],
                            ["Address", viewAddr.address],
                            ["Suburb", viewAddr.city],
                            ["State", viewAddr.state],
                            ["Postcode", viewAddr.addressPincode],
                            ["User phone", viewAddr.user_id?.phoneNumber || "—"],
                            ["Added", moment(viewAddr.createdAt).format("DD/MM/YYYY HH:mm")],
                          ].map(([label, value]) => (
                            <Col xxl={6} md={6} className="mb-2" key={label}>
                              <p className="mb-0 text-xs text-[#1D243399]">{label}</p>
                              <p className="mb-0 text-sm font-medium">{value || "—"}</p>
                            </Col>
                          ))}

                          {viewAddr.coordinates?.lat !== 0 && (
                            <Col xxl={12} className="mt-2">
                              <a
                                href={viewAddr.map_url || `https://www.google.com/maps?q=${viewAddr.coordinates?.lat},${viewAddr.coordinates?.lng}`}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: "#D3178A", fontSize: "0.8rem", fontWeight: 600 }}
                              >
                                📍 View on Google Maps
                              </a>
                            </Col>
                          )}

                          <Col xxl={12} className="mt-4 d-flex gap-2 justify-content-end">
                            {viewAddr.approval_status !== "approved" && (
                              <button type="button"
                                onClick={() => { setViewModal(false); handleApprove(viewAddr); }}
                                style={{
                                  background: "#16a34a", color: "#fff", border: "none",
                                  borderRadius: "12px", padding: "8px 20px",
                                  fontWeight: 600, cursor: "pointer",
                                }}>
                                Approve
                              </button>
                            )}
                            {viewAddr.approval_status !== "rejected" && (
                              <button type="button"
                                onClick={() => { setViewModal(false); openReject(viewAddr); }}
                                style={{
                                  background: "#dc2626", color: "#fff", border: "none",
                                  borderRadius: "12px", padding: "8px 20px",
                                  fontWeight: 600, cursor: "pointer",
                                }}>
                                Reject
                              </button>
                            )}
                            <button type="button" onClick={() => setViewModal(false)}
                              style={{
                                background: "#f3f4f6", color: "#374151", border: "none",
                                borderRadius: "12px", padding: "8px 20px",
                                fontWeight: 600, cursor: "pointer",
                              }}>
                              Close
                            </button>
                          </Col>
                        </Row>
                      </Container>
                    )}
                  </CommonModal>

                  {/* ── Reject modal ──────────────────────────────────────────── */}
                  <CommonModal
                    isOpen={rejectModal}
                    title="Reject Address"
                    toggler={() => setRejectModal(false)}
                    size="md"
                  >
                    <Container>
                      {rejectTarget && (
                        <p className="text-sm text-[#1D243399] mb-3">
                          Rejecting: <strong>{rejectTarget.address}</strong>
                        </p>
                      )}
                      <FormGroup>
                        <Label>Reason (shown to customer)</Label>
                        <Input
                          type="textarea" rows={3}
                          value={rejectNote}
                          onChange={(e) => setRejectNote(e.target.value)}
                          placeholder="e.g. Outside our current delivery zone. We're expanding soon — check back in a few weeks."
                        />
                      </FormGroup>
                      <div className="d-flex justify-content-end gap-2 mt-3">
                        <button type="button" onClick={() => setRejectModal(false)}
                          style={{
                            background: "#f3f4f6", color: "#374151", border: "none",
                            borderRadius: "12px", padding: "8px 20px", fontWeight: 600, cursor: "pointer",
                          }}>
                          Cancel
                        </button>
                        <button type="button" onClick={handleRejectSubmit}
                          style={{
                            background: "#dc2626", color: "#fff", border: "none",
                            borderRadius: "12px", padding: "8px 20px", fontWeight: 600, cursor: "pointer",
                          }}>
                          Confirm Reject
                        </button>
                      </div>
                    </Container>
                  </CommonModal>
                </Fragment>

              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
}